
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { UserRoundCheck, Phone, MailCheck, Shield, PhoneIncoming } from "lucide-react";
import { Candidate } from "../candidates/types";
import { useScreeningAgent } from "@/hooks/a2a/useScreeningAgent";
import { CandidateInfo } from "./dialog/CandidateInfo";
import { PhoneScreenTab } from "./dialog/PhoneScreenTab";
import { TechnicalInterviewTab } from "./dialog/TechnicalInterviewTab";
import { FraudDetectionTab } from "./dialog/FraudDetectionTab";
import { InboundScreeningDashboard } from "./InboundScreeningDashboard";
import { prepareResumeData } from "./utils/resumeDataUtils";
import { useScreeningTabs } from "./hooks/useScreeningTabs";

interface ScreeningDialogProps {
  open: boolean;
  onClose: () => void;
  candidate: Candidate;
  jobTitle?: string;
}

export function ScreeningDialog({ open, onClose, candidate, jobTitle = "Software Developer" }: ScreeningDialogProps) {
  const { 
    conductLivePhoneScreen,
    deepRoleInterview,
    runFraudDetection,
    phoneScreening,
    deepInterview,
    fraudDetection,
    isLoading
  } = useScreeningAgent();

  const { activeTab, setActiveTab } = useScreeningTabs(phoneScreening, deepInterview);

  const handleStartPhoneScreening = async () => {
    await conductLivePhoneScreen({
      candidateId: candidate.id,
      candidateName: `${candidate.first_name} ${candidate.last_name}`,
      candidatePhone: candidate.phone || '+15551234567',
      jobTitle: jobTitle || candidate.current_role || 'Software Developer'
    });
  };

  const handleRunTechnicalAssessment = async () => {
    await deepRoleInterview({
      candidateId: candidate.id,
      jobTitle: jobTitle || candidate.current_role || 'Software Developer',
      round: 0,
      questionPayload: { focus: 'technical' }
    });
  };

  const handleDetectFraud = async () => {
    await runFraudDetection({
      candidateId: candidate.id,
      transcript: phoneScreening.transcript || "",
      resumeData: prepareResumeData(candidate),
      candidateLocation: {
        ip: "192.168.1.1",
        geolocation: {
          latitude: 40.7128,
          longitude: -74.0060
        },
        city: "New York",
        country: "USA"
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRoundCheck className="h-5 w-5" /> 
            Advanced Candidate Screening
          </DialogTitle>
          <DialogDescription>
            Complete screening assessment for {candidate.first_name} {candidate.last_name}
          </DialogDescription>
        </DialogHeader>
        
        <CandidateInfo candidate={candidate} />
        
        <Tabs 
          defaultValue="phone" 
          value={activeTab} 
          onValueChange={(value) => setActiveTab(value as any)}
        >
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" /> Phone Screen
            </TabsTrigger>
            <TabsTrigger value="interview" className="flex items-center gap-2">
              <MailCheck className="h-4 w-4" /> Tech Interview
            </TabsTrigger>
            <TabsTrigger value="fraud" className="flex items-center gap-2">
              <Shield className="h-4 w-4" /> Fraud Check
            </TabsTrigger>
            <TabsTrigger value="inbound" className="flex items-center gap-2">
              <PhoneIncoming className="h-4 w-4" /> Inbound Calls
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="phone">
            <PhoneScreenTab 
              phoneScreening={phoneScreening}
              isLoading={isLoading}
              onStartScreening={handleStartPhoneScreening}
            />
          </TabsContent>
          
          <TabsContent value="interview">
            <TechnicalInterviewTab
              deepInterview={deepInterview}
              isLoading={isLoading}
              onStartInterview={handleRunTechnicalAssessment}
            />
          </TabsContent>
          
          <TabsContent value="fraud">
            <FraudDetectionTab
              fraudDetection={fraudDetection}
              isLoading={isLoading}
              onStartDetection={handleDetectFraud}
            />
          </TabsContent>
          
          <TabsContent value="inbound">
            <InboundScreeningDashboard />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
